import { Search } from "common/assets";
import {
  Box,
  Button,
  FormControl,
  FormTextField,
  Grid2,
  Pagination,
} from "common/components";
import { CardsSelectionType, CardsSize } from "core/actions/flows/steps/Cards";
import { createWildcardRegex, NoListData } from "core/components";
import { useTranslation } from "i18n";
import {
  ChangeEvent,
  forwardRef,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { Control, useFormContext } from "react-hook-form";
import { theme } from "styles/theme";
import CardOption from "../../../../CardOption";
import { transformToCardOptionData } from "../../../../CardOption/CardOption";

interface SearchableCardOptionsFieldProps {
  /** Form control */
  control: Control;
  /** List of options */
  options: any[];
  /** The selected index. */
  selectedIndices: number[];
  /** Function to set the selected index. */
  setSelectedIndices: (value: React.SetStateAction<number[]>) => void;
  /** Size of cards */
  cardSize: CardsSize;
  /** If single or multi select */
  selectionType: CardsSelectionType;
  /** If an error according to the validation. Will display error text if true. */
  error?: boolean;
}

/**
 * Field with FormControl that displays CardOptions with search and pagination.
 *
 */
export const SearchableCardOptionsField = forwardRef<
  HTMLDivElement,
  SearchableCardOptionsFieldProps
>(
  (
    {
      options,
      control,
      selectedIndices,
      setSelectedIndices,
      selectionType,
      cardSize,
      ...props
    }: SearchableCardOptionsFieldProps,
    ref
  ): ReactElement => {
    const { t } = useTranslation(["common"]);
    const [page, setPage] = useState(1);
    // Search query state for filtering
    const [searchQuery, setSearchQuery] = useState<string>("*");
    // items to display per page
    const pageSize = 6; // needs to later work as for the table where the user can change, or adjust based on card size

    const formContext = useFormContext();
    const [hasFiltered, setHasFiltered] = useState(false);

    const handlePageChange = (_event: ChangeEvent<unknown>, value: number) => {
      setPage(value);
    };

    // This is just the input, it doesn't affect filtering until search is clicked
    const searchValue: string = formContext.watch("searchField") ?? "*";

    // Filter options based on searchQuery (set when search button is clicked)
    const filteredOptions = options?.filter((option) => {
      if (searchQuery === "*") {
        return true;
      }

      // needs to be adapted for when support for Product offering options
      const optionName = option.name.toLowerCase() || "";

      // If the searchQuery contains "*", use it as a wildcard for partial matching
      if (searchQuery.includes("*")) {
        const searchPattern = createWildcardRegex(searchQuery);
        return searchPattern.test(optionName);
      }

      return optionName.toLowerCase() === searchQuery.toLowerCase();
    });

    // Paginate options after filtering
    const paginatedOptions = filteredOptions?.slice(
      (page - 1) * pageSize,
      page * pageSize
    );
    const numPages = Math.ceil(filteredOptions?.length / pageSize);

    /**
     * Handles the search button click event.
     * Updates the `searchQuery` state with the current `searchValue`,
     * resets pagination to the first page, and marks the search as filtered.
     * Optionally clears the selection based on the `autoSearch` parameter.
     *
     * @param autoSearch - Indicates whether the search is triggered automatically.
     *
     */
    const handleSearchClick = (autoSearch = false) => {
      setSearchQuery(searchValue); // Set searchQuery to the value in the search input
      setPage(1);
      setHasFiltered(true);

      if (!autoSearch || options?.length === 0) {
        setSelectedIndices([]); // was -1 here before, does this as planned?
      }
    };

    // auto search when first loading the component
    useEffect(() => {
      handleSearchClick(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchField = (
      <FormTextField
        control={control}
        defaultValue={searchValue}
        fieldName={"searchField"}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleSearchClick();
          }
        }}
        data-cy="formSearchField"
        sx={{ minWidth: 320 }}
      />
    );

    return (
      <FormControl {...props} ref={ref}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacing(1),
            marginBottom: theme.spacing(0.5),
            alignItems: "center",
          }}
        >
          {searchField}
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleSearchClick()}
            data-cy="formSearchButton"
            sx={{ height: 36 }}
          >
            <Search />
          </Button>
        </Box>

        {!paginatedOptions || paginatedOptions.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: theme.spacing(1),
            }}
          >
            <NoListData variant="data" />
          </Box>
        ) : (
          hasFiltered && (
            <>
              <Grid2 container spacing={1}>
                {paginatedOptions?.map((option) => {
                  // Calculate the global index relative to the original options array
                  const globalIndex = options.indexOf(option);
                  return (
                    <Grid2 key={globalIndex}>
                      <CardOption
                        cardOptionData={transformToCardOptionData(option, t)}
                        index={globalIndex}
                        selected={selectedIndices.includes(globalIndex)}
                        setSelectedIndices={setSelectedIndices}
                        selectionType={selectionType}
                        cardSize={cardSize}
                      />
                    </Grid2>
                  );
                })}
              </Grid2>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  marginTop: theme.spacing(0.5),
                }}
              >
                <Pagination
                  count={numPages ?? 0}
                  page={page}
                  onChange={handlePageChange}
                />
              </Box>
            </>
          )
        )}
      </FormControl>
    );
  }
);
